<template>
  <WModal
    v-model="isOpen"
    head-line="Dashboard target"
    :icon="mdiMonitorDashboard"
    confirm-text="Save"
    :confirm-disabled="!selectedTarget"
    max-width="560px"
    :section-tag="sectionTag"
    @confirm="handleSave"
    @cancel="isOpen = false"
  >
    <!-- WModal renders #body only alongside bodyText; free-form content goes in #content. -->
    <template #content>
      <p class="body-regular-default mb-4" :data-testid="`${sectionTag}-intro`">
        Where Login as merchant opens the merchant dashboard from this tab. New tabs start on Default.
      </p>

      <WRadioGroup v-model="selectedKey" :section-tag="`${sectionTag}-options`">
        <WRadioBtn value="default" label="Default" :section-tag="`${sectionTag}-option-default`" />
        <WRadioBtn
          v-if="isDev"
          value="local"
          label="Local dev"
          :section-tag="`${sectionTag}-option-local`"
        />

        <template v-if="isStaging">
          <div class="deployment-controls">
            <WInputField
              v-model="search"
              label="Search branches"
              search
              class="deployment-search"
              :section-tag="`${sectionTag}-search`"
            />
            <WSwitch
              v-model="showAll"
              on-label="Show all"
              off-label="Show all"
              aria-label="Show all deployments"
              :section-tag="`${sectionTag}-show-all`"
            />
          </div>

          <div v-if="deploymentsStore.isLoading" class="deployment-message">
            <WLoader :section-tag="`${sectionTag}-loading`" />
          </div>
          <p
            v-else-if="!deploymentsStore.isAvailable"
            class="deployment-message body-regular-default"
            :data-testid="`${sectionTag}-not-available`"
          >
            Deployments are not available in this environment yet.
          </p>
          <div
            v-else-if="deploymentsStore.hasLoadFailed"
            class="deployment-message"
            :data-testid="`${sectionTag}-error`"
          >
            <p class="body-regular-default">Could not load deployments.</p>
            <WButton
              variant="link"
              label="Try again"
              :section-tag="`${sectionTag}-retry`"
              @click="deploymentsStore.fetchDeployments()"
            />
          </div>
          <p
            v-else-if="visibleDeployments.length === 0"
            class="deployment-message body-regular-default"
            :data-testid="`${sectionTag}-empty`"
          >
            {{ emptyMessage }}
          </p>
          <div v-else class="deployment-list">
            <WRadioBtn
              v-for="deployment in visibleDeployments"
              :key="deployment.branch"
              :value="deploymentKey(deployment.branch)"
              :section-tag="`${sectionTag}-option-${deployment.branch}`"
            >
              <template #label>
                <span class="deployment-label">
                  <span class="deployment-branch">{{ deployment.branch }}</span>
                  <span class="deployment-updated body-regular-small">
                    {{ formatRelativeTime(deployment.updatedAt) }}
                  </span>
                </span>
              </template>
            </WRadioBtn>
          </div>

          <p
            v-if="isListShown"
            class="deployment-help body-regular-small"
            :data-testid="`${sectionTag}-help`"
          >
            {{ listHelpText }}
          </p>
        </template>
      </WRadioGroup>
    </template>
  </WModal>
</template>

<script setup lang="ts">
/**
 * Dashboard target picker — chooses where Login as merchant opens the merchant dashboard:
 * Default, Local dev (dev ADX), or a branch deployment from the list (staging ADX).
 *
 * @example
 * <DashboardTargetPicker v-model="showPicker" section-tag="dashboard-target-picker" />
 */
import { ref, computed, watch } from 'vue'

import { useUiSettingsStore } from '@/stores/ui-settings.store'
import { useDashboardDeploymentsStore } from '@/stores/dashboard-deployments.store'

import { useStoreErrorWatch } from '@/composables/useStoreErrorWatch'

import type { DashboardTarget } from '@/types/ui-settings.types'

import WButton from '@wasabi/actions/WButton.vue'
import WInputField from '@wasabi/inputs/WInputField.vue'
import WRadioBtn from '@wasabi/inputs/WRadioBtn.vue'
import WRadioGroup from '@wasabi/inputs/WRadioGroup.vue'
import WSwitch from '@wasabi/inputs/WSwitch.vue'
import WModal from '@wasabi/content-displays/WModal.vue'
import WLoader from '@wasabi/data-visualization/WLoader.vue'

import { mdiMonitorDashboard } from '@mdi/js'

import { filterDeploymentsForPicker, RECENT_DEPLOYMENT_WINDOW_DAYS } from '@/utils/dashboardTarget'
import { formatRelativeTime } from '@/utils/dateUtils'

interface Props {
  sectionTag?: string
}

withDefaults(defineProps<Props>(), {
  sectionTag: 'dashboard-target-picker'
})

const isOpen = defineModel<boolean>({ default: false })

// ============ STORES ============
const uiSettingsStore = useUiSettingsStore()
const deploymentsStore = useDashboardDeploymentsStore()

// ============ COMPOSABLES ============
useStoreErrorWatch(deploymentsStore, 'Error Loading Deployments')

// ============ REACTIVE STATE ============
const selectedKey = ref<string>('default')
const search = ref('')
const showAll = ref(false)

// ============ COMPUTED ============
const isDev = computed(() => uiSettingsStore.currentEnvironment === 'DEV')
const isStaging = computed(() => uiSettingsStore.currentEnvironment === 'STAGING')

const visibleDeployments = computed(() =>
  filterDeploymentsForPicker(deploymentsStore.deployments, {
    showAll: showAll.value,
    search: search.value
  })
)

const emptyMessage = computed(() => {
  if (search.value.trim()) return 'No deployments match that branch name.'
  return showAll.value
    ? 'No deployments are available.'
    : `No deployments were updated in the last ${RECENT_DEPLOYMENT_WINDOW_DAYS} days. Turn on Show all to see older ones.`
})

// The list, or its empty state, is on screen: not loading, offered here, and not failed.
const isListShown = computed(
  () => !deploymentsStore.isLoading && deploymentsStore.isAvailable && !deploymentsStore.hasLoadFailed
)

// Amplify deletes a branch's deployment when the branch itself is deleted, usually on PR merge.
const listHelpText = computed(() => {
  const scope = showAll.value
    ? 'Only branches deployed from the staging backend.'
    : `Only branches deployed in the last ${RECENT_DEPLOYMENT_WINDOW_DAYS} days, from the staging backend.`
  return `${scope} A branch disappears once it's deleted, usually when its PR merges.`
})

// Only an entry from the fetched list can become a deployment target, never typed input.
const selectedTarget = computed<DashboardTarget | null>(() => {
  if (selectedKey.value === 'default') return { kind: 'default' }
  if (selectedKey.value === 'local') return isDev.value ? { kind: 'local' } : null
  const deployment = deploymentsStore.selectableDeployments.find(
    candidate => deploymentKey(candidate.branch) === selectedKey.value
  )
  return deployment ? { kind: 'deployment', branch: deployment.branch, url: deployment.url } : null
})

// ============ METHODS ============
function deploymentKey(branch: string): string {
  return `deployment:${branch}`
}

function keyForTarget(target: DashboardTarget): string {
  if (target.kind === 'deployment') return deploymentKey(target.branch)
  return target.kind
}

function handleSave() {
  if (!selectedTarget.value) return
  uiSettingsStore.setDashboardTarget(selectedTarget.value)
  isOpen.value = false
}

// ============ WATCHERS ============
watch(
  isOpen,
  open => {
    if (!open) return
    selectedKey.value = keyForTarget(uiSettingsStore.activeDashboardTarget)
    search.value = ''
    showAll.value = false
    if (isStaging.value) {
      deploymentsStore.fetchDeployments()
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.deployment-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 12px 0 8px;
}

.deployment-search {
  flex: 1;
}

.deployment-list {
  max-height: 320px;
  overflow-y: auto;
}

.deployment-message {
  padding: 16px 0;
}

.deployment-label {
  display: flex;
  flex-direction: column;
}

.deployment-branch {
  word-break: break-all;
}

.deployment-updated,
.deployment-help {
  opacity: 0.7;
}

.deployment-help {
  margin-top: 8px;
}
</style>
